import { api, API_URL, authStore, apiFetch, camelToSnake, snakeToCamel } from '@/core/api/client'

import type { AttendeeStatus, CloseSessionResult, OpenCheckinPayload, SessionScheduleSource, SessionStatus } from '../stores'

export interface PBSession {
  id: string
  qrToken: string
  courseId: string
  courseName?: string
  courseSlug?: string
  location: string
  locationName?: string
  locationId?: string
  date: string
  startTime?: string
  endTime?: string
  durationMinutes: number
  status: SessionStatus
  instructorName: string
  instructorId?: string
  maxAttendees?: number
  spotsAvailable?: number
  attendeeCount?: number
  language: string
  startedAt: string | null
  closedAt: string | null
  scheduleSource?: SessionScheduleSource
}

export interface ExistingAttendee {
  id: string
  firstName: string
  lastName: string
  studentEmail: string
  phone: string
  company: string
  status: AttendeeStatus
  checkedInAt: string | null
  quizScore: number | null
}

export interface OpenCheckinResponse {
  detail: string
  emails_sent: number
  emailed_to: string[]
  failed_count: number
  failed_to: Array<{ email: string; reason: string }>
}

const sessionsCollection = api.collection<PBSession>('sessions')

/** Fetch all sessions. */
export async function getAllSessions(): Promise<PBSession[]> {
  return sessionsCollection.getFullList()
}

/** Validate QR token format. */
function isValidQrToken(token: string): boolean {
  return /^[a-zA-Z0-9_-]{8,128}$/.test(token)
}

/** Fetch a session by its QR token. No authentication required – used by the public student join flow.
 *  Uses the dedicated backend endpoint instead of loading all sessions client-side.
 *  When ``email`` is provided, the backend also returns the matching attendee record
 *  so pre-registered students don't have to re-enter their data.
 */
export async function getSessionByQrToken(
  qrToken: string,
  email?: string,
): Promise<{ session: PBSession; attendee: ExistingAttendee | null } | null> {
  if (!isValidQrToken(qrToken)) {
    console.warn('[sessionsService] Invalid QR token format')
    return null
  }
  try {
    const params = new URLSearchParams()
    params.set('token', qrToken)
    if (email) {
      params.set('email', email.toLowerCase().trim())
    }
    const res = await fetch(`${API_URL}/sessions/by-qr-token/?${params.toString()}`)
    if (!res.ok) {
      if (res.status === 404) {return null}
      throw new Error(`Failed to fetch session by QR token: ${res.status}`)
    }
    const data = await res.json()
    const session: PBSession = {
      id: data.id,
      qrToken: data.qr_token ?? data.qrToken,
      courseId: data.course_id ?? data.courseId,
      courseName: data.course_name ?? data.courseName,
      courseSlug: data.course_slug ?? data.courseSlug,
      location: data.location,
      locationName: data.location_name ?? data.locationName,
      date: data.date,
      durationMinutes: data.duration_minutes ?? data.durationMinutes,
      status: data.status,
      instructorName: data.instructor_name ?? data.instructorName,
      maxAttendees: data.max_attendees ?? data.maxAttendees,
      spotsAvailable: data.spots_available ?? data.spotsAvailable,
      language: data.language ?? 'any',
      startedAt: data.started_at ?? data.startedAt ?? null,
      closedAt: data.closed_at ?? data.closedAt ?? null,
      scheduleSource: data.schedule_source ?? data.scheduleSource,
    }

    const attendeeData = data.attendee
    const attendee: ExistingAttendee | null = attendeeData
      ? {
          id: String(attendeeData.id),
          firstName: String(attendeeData.first_name ?? attendeeData.firstName ?? ''),
          lastName: String(attendeeData.last_name ?? attendeeData.lastName ?? ''),
          studentEmail: String(
            attendeeData.student_email ?? attendeeData.studentEmail ?? attendeeData.studentId ?? ''
          ),
          phone: String(attendeeData.phone ?? ''),
          company: String(attendeeData.company ?? ''),
          status: String(attendeeData.status) as AttendeeStatus,
          checkedInAt: (attendeeData.checked_in_at ?? attendeeData.checkedInAt ?? null) as string | null,
          quizScore: (attendeeData.quiz_score ?? attendeeData.quizScore ?? null) as number | null,
        }
      : null

    return { session, attendee }
  } catch (err: unknown) {
    if (typeof err === 'object' && err !== null && 'status' in err) {
      const status = (err as { status: number }).status
      if (status === 401 || status === 403) {
        throw err
      }
    }
    return null
  }
}

/** Fetch a single session by ID. */
export async function getSession(id: string): Promise<PBSession> {
  return sessionsCollection.getOne(id)
}

/** Create a new session record. */
export async function createSession(data: {
  qrToken: string
  courseId: string
  location: string
  date: string
  startTime?: string
  endTime?: string
  status: SessionStatus
  instructorName: string
  maxAttendees?: number
  startedAt: string | null
  closedAt: string | null
}): Promise<PBSession> {
  return sessionsCollection.create(data)
}

/** Update an existing session. */
export async function updateSession(
  id: string,
  data: Partial<{
    qrToken: string
    courseId: string
    location: string
    locationId: string
    date: string
    startTime: string
    endTime: string
    status: SessionStatus
    instructorName: string
    instructorId: string
    maxAttendees?: number
    startedAt: string | null
    closedAt: string | null
  }>
): Promise<PBSession> {
  return sessionsCollection.update(id, data)
}

/** Open check-in for a session via the dedicated backend action.
 *  This moves the session to 'checking' and emails registered/paid attendees.
 */
export async function openCheckinSession(
  id: string,
  payload: OpenCheckinPayload = {}
): Promise<OpenCheckinResponse> {
  const body = camelToSnake(payload)
  const res = await apiFetch(`/sessions/${encodeURIComponent(id)}/open_checkin/`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Failed to open check-in: ${res.status} ${text}`)
  }
  return res.json()
}

/** Close a session via the dedicated backend action.
 *  This triggers certificate generation and emails. Pass ``attendeeIds`` to
 *  generate certificates only for the selected attendees.
 */
export async function closeSession(
  id: string,
  attendeeIds?: string[]
): Promise<CloseSessionResult> {
  const body: Record<string, unknown> = {}
  if (attendeeIds !== undefined) {
    body.attendee_ids = attendeeIds
  }
  const res = await fetch(`${API_URL}/sessions/${encodeURIComponent(id)}/close/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {}),
    },
    body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Failed to close session: ${res.status} ${text}`)
  }
  return snakeToCamel<CloseSessionResult>(await res.json())
}

/** Delete a session. */
export async function deleteSession(id: string): Promise<void> {
  await sessionsCollection.delete(id)
}

